import { EMPTY, Observable, defer, expand, map, reduce } from 'rxjs';

/**
 * Mirrors mtm-backend `ProjectService.USER_PROJECTS_PAGE_SIZE`. The user-project listings return a
 * bare array per page with no hasNext envelope; the backend guarantees a page shorter than this
 * marks the end of the listing (filters are applied while filling the page, never after).
 */
export const USER_PROJECTS_PAGE_SIZE: number = 10;

/**
 * Aggregates a 1-based paged endpoint that returns a bare array per page: pages are fetched
 * serially until one comes back shorter than pageSize, and the concatenation is emitted once.
 * Items are deduped by `id`, and a page yielding nothing new also ends the walk — so a backend
 * that ignores the page param (serving the full list on every page) terminates after two
 * requests instead of looping forever.
 * Note: cross-page order is the backend's id order — re-sort after aggregation where order matters.
 */
export function fetchAllPages<T>(
  fetchPage: (page: number) => Observable<T[]>,
  pageSize: number = USER_PROJECTS_PAGE_SIZE
): Observable<T[]> {
  return defer(() => {
    const seenKeys = new Set<string>();
    const dedupe = (items: T[]): { fresh: T[]; rawCount: number } => {
      const raw = Array.isArray(items) ? items : [];
      const fresh = raw.filter((item) => {
        const key = String((item as { id?: unknown })?.id ?? JSON.stringify(item));
        if (seenKeys.has(key)) {
          return false;
        }
        seenKeys.add(key);
        return true;
      });
      return { fresh, rawCount: raw.length };
    };
    return fetchPage(1).pipe(
      map(dedupe),
      expand((page, index) =>
        page.rawCount < pageSize || !page.fresh.length ? EMPTY : fetchPage(index + 2).pipe(map(dedupe))
      ),
      reduce((all: T[], page) => all.concat(page.fresh), [] as T[])
    );
  });
}
